import { FixedSizeList } from 'react-window';
import { useRef } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DotsHorizontalIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { formatDate } from '@/lib/utils/dates';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { FormatedTransaction } from '../../transactions/types';
import type { Row } from '@tanstack/react-table';
import { History } from 'lucide-react';

export type ItemType = {
  pk(pk: any): void;
  id: string;
  stage: string;
  status: string;
  transactionDate: string;
  effectiveDate: string;
};

type RowsProps = {
  items: Array<Row<FormatedTransaction>>;
  activeId: string | undefined;
  handleTimeTravel: (pk: FormatedTransaction) => void;
  handleEdit: (pk: string) => void;
  editMode: boolean;
};

const TransactionRow = ({
  index,
  style,
  data,
}: {
  index: number;
  style: React.CSSProperties | undefined;
  data: RowsProps;
}) => {
  const { items, activeId, handleEdit, handleTimeTravel, editMode } = data;

  const entry = items[index].original;
  const inProgressTransaction = 'in_progress';

  return (
    <div style={style} className="pb-3">
      <Card
        key={entry.transactionId}
        className={`mb-3 h-full cursor-pointer border-0 p-[16px] ${activeId === String(entry.transactionNumber) ? 'bg-brand-dark-green text-white' : 'bg-brand-100'}`}
        onClick={() => handleEdit(String(entry.transactionNumber))}
      >
        <div className="flex h-full justify-between">
          <div className="flex flex-col">
            <div className="flex text-[16px]">
              <p>ID:&nbsp;</p>
              <p>{entry.transactionId}</p>
            </div>
            <div className="mt-auto flex flex-col text-[12px]">
              <div className="flex">
                <p>Stage:&nbsp;</p>
                <p className="capitalize">{entry.stage}</p>
              </div>
              <div className="flex">
                <p>Status:&nbsp;</p>
                <p className="capitalize">{entry.status}</p>
              </div>
              <div className="flex">
                <p>Transaction Date:&nbsp;</p>
                <p>
                  {entry.transactionDate
                    ? formatDate(entry.transactionDate)
                    : 'No data'}
                </p>
              </div>
              <div className="flex">
                <p>Effective Date:&nbsp;</p>
                <p>
                  {entry.effectiveDate
                    ? formatDate(entry.effectiveDate)
                    : 'No data'}
                </p>
              </div>
            </div>
          </div>
          <div className="relative right-2">
            {entry.status !== inProgressTransaction ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-auto w-auto self-start p-0"
                  >
                    <span className="sr-only">Open menu</span>
                    <DotsHorizontalIcon className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleTimeTravel(entry)}>
                    <div className="flex cursor-pointer items-center gap-2">
                      <History size={24} strokeWidth={1} />
                      <p className="text-[14px]">Time travel to snapshot</p>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>
        <div className="flex"></div>
      </Card>
    </div>
  );
};

const columnHelper = createColumnHelper<FormatedTransaction>();
const columns = [
  columnHelper.accessor('transactionId', {
    cell: ({ row }) => row.original.transactionId,
  }),
  columnHelper.accessor('effectiveDate', {
    cell: ({ row }) => formatDate((row.original.effectiveDate ?? '') as string),
  }),
  columnHelper.accessor('transactionDate', {
    cell: ({ row }) =>
      formatDate((row.original.transactionDate as unknown as string) ?? ''),
  }),
  columnHelper.accessor('status', {
    cell: ({ row }) => row.original.status,
  }),
  columnHelper.accessor('stage', {
    cell: ({ row }) => row.original.stage,
  }),
];

export const List = ({
  items,
  activeId,
  handleTimeTravel,
  handleEdit,
  editMode,
}: {
  items: Array<FormatedTransaction> | null;
  activeId: string | undefined;
  handleTimeTravel: (pk: FormatedTransaction) => void;
  handleEdit: (pk: string) => void;
  editMode: boolean;
}) => {
  const listRef = useRef<FixedSizeList>(null);

  const table = useReactTable<FormatedTransaction>({
    columns,
    data: items || [],
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',
  });

  const { rows } = table.getRowModel();

  return (
    <>
      <div className="flex flex-row items-center gap-1 rounded-md border border-solid border-brand-200 px-2">
        <MagnifyingGlassIcon width={24} height={24} />
        <input
          name="searchString"
          placeholder="Search..."
          onChange={(e) => table.setGlobalFilter(e.target.value)}
          className="w-full p-1"
        />
      </div>

      {rows?.length ? (
        <FixedSizeList
          ref={listRef}
          height={1500}
          itemCount={rows.length}
          itemSize={160}
          width={320}
          itemData={{
            items: rows,
            handleTimeTravel,
            activeId,
            handleEdit,
            editMode,
          }}
        >
          {TransactionRow}
        </FixedSizeList>
      ) : (
        ''
      )}
    </>
  );
};

export default List;
